import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { api, getTokens, onTokensChange, setTokens } from "./api";
import { forgetSessions } from "./appSessions";
import { captureReset } from "./capture";
import { resetEvents } from "./events";
import { clearInstalled } from "./installer";
import { clearPrefixes } from "./storage";
import type { User } from "./types";

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; display_name: string; invite_code?: string }) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (u: User) => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Fill fields an older server may not send yet, so screens never crash on them. */
export function normalizeUser(u: User): User {
  return {
    ...u,
    links: u.links ?? [],
    contacts: u.contacts ?? [],
    cover_url: u.cover_url ?? "",
    avatar_url: u.avatar_url ?? "",
    avatar_crop: u.avatar_crop ?? null,
    cover_crop: u.cover_crop ?? null,
    has_avatar_source: !!u.has_avatar_source,
    has_cover_source: !!u.has_cover_source,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setRawUser] = useState<User | null>(null);
  const setUser = useCallback((u: User | null) => setRawUser(u ? normalizeUser(u) : null), []);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!getTokens()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api<User>("/api/me"));
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    loadMe();
    return onTokensChange(() => {
      if (!getTokens()) setUser(null);
    });
  }, [loadMe, setUser]);

  const value: AuthState = {
    user,
    loading,
    setUser,
    async login(email, password) {
      const res = await api<{ access_token: string; refresh_token: string; user: User }>("/api/auth/login", {
        body: { email, password },
        auth: false,
      });
      setTokens(res);
      setUser(res.user);
    },
    async register(data) {
      const res = await api<{ access_token: string; refresh_token: string; user: User }>("/api/auth/register", {
        body: data,
        auth: false,
      });
      setTokens(res);
      setUser(res.user);
    },
    async logout() {
      const t = getTokens();
      if (t) await api("/api/auth/logout", { body: { refresh_token: t.refresh_token }, auth: false }).catch(() => {});
      // Nothing of this person stays on the device: app tokens, installed apps, event position
      forgetSessions();
      resetEvents();
      await captureReset(); // the phone stops recording for this person
      clearPrefixes("omni.widget:", "omni.appstore.");
      await clearInstalled().catch(() => undefined);
      setTokens(null);
      setUser(null);
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
